import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, qs } from '../lib/api';
import { Badge, Button, Card, Field, Loading, PageHeader, Select } from '../components/ui/primitives';
import { dateTime } from '../lib/format';
import type { AnsweredQuestion, ClientRecord } from '../lib/types';

/**
 * The seven supplier due-diligence questions, answered from live system data
 * rather than from a slide deck. Export produces a single document that can
 * be sent to the client as-is.
 */
export function ClientQuestionsPage() {
  const [clientId, setClientId] = useState('');
  const [days, setDays] = useState(90);

  const { data: clients } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get<{ clients: ClientRecord[] }>('/api/clients'),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['client-questions', clientId, days],
    queryFn: () =>
      api.get<{ generatedAt: string; questions: AnsweredQuestion[] }>(
        '/api/analytics/client-questions' + qs({ clientId, days })
      ),
  });

  const download = () => {
    if (!data) return;
    const clientName = clients?.clients.find((c) => c.id === clientId)?.name ?? 'All clients';
    const lines: string[] = [
      'Automated activity filtering and Microsoft deliverability',
      'Scope: ' + clientName + ', last ' + days + ' days',
      'Generated: ' + new Date(data.generatedAt).toISOString(),
      '',
    ];
    for (const q of data.questions) {
      lines.push('Q: ' + q.question);
      lines.push('A (' + q.answer.toUpperCase() + '): ' + q.summary);
      for (const d of q.detail) lines.push('  - ' + d);
      lines.push('');
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'deliverability-answers.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <PageHeader
        title="Client answers"
        description="The questions clients ask when auditing a lead-gen supplier, answered from what the platform is actually doing right now. Every figure quoted comes from the live data for the selected scope."
        actions={
          <Button variant="primary" onClick={download} disabled={!data}>
            Export for the client
          </Button>
        }
      />

      <div className="mb-5 flex flex-wrap items-end gap-3">
        <Field label="Client">
          <Select value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">All clients</option>
            {(clients?.clients ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Period">
          <Select value={days} onChange={(e) => setDays(Number(e.target.value))}>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 12 months</option>
          </Select>
        </Field>
      </div>

      {isLoading || !data ? (
        <Loading />
      ) : (
        <div className="space-y-5">
          {data.questions.map((q, index) => (
            <Card key={q.id}>
              <div className="flex items-start gap-4">
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ink-900 text-xs font-semibold text-white">
                  {index + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <p className="max-w-3xl text-sm font-semibold leading-relaxed text-ink-900">
                      {q.question}
                    </p>
                    <Badge tone={q.answer === 'yes' ? 'good' : q.answer === 'partial' ? 'warn' : 'bad'}>
                      {q.answer === 'yes' ? 'Yes' : q.answer === 'partial' ? 'Partially' : 'No'}
                    </Badge>
                  </div>

                  <p className="mt-3 text-sm leading-relaxed text-ink-700">{q.summary}</p>

                  <ul className="mt-3 space-y-1.5">
                    {q.detail.map((d, i) => (
                      <li key={i} className="flex gap-2 text-xs leading-relaxed text-ink-600">
                        <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-ink-400" />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>

                  <details className="mt-3">
                    <summary className="cursor-pointer text-xs font-medium text-ink-500 hover:text-ink-900">
                      Supporting data
                    </summary>
                    <pre className="mt-2 max-h-80 overflow-auto rounded-lg bg-ink-900 p-3 text-[11px] leading-relaxed text-ink-100">
                      {JSON.stringify(q.evidence, null, 2)}
                    </pre>
                  </details>
                </div>
              </div>
            </Card>
          ))}

          <p className="text-xs text-ink-400">Generated {dateTime(data.generatedAt)}</p>
        </div>
      )}
    </>
  );
}
